import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Icon,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from "antd";
import moment from "moment";
import axios from "util/Api";
import { createDebounce } from "../../util/methods";
import "./style.css";

const toId = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "object" && value._id) {
    return value._id.toString();
  }
  return value.toString();
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const pickSubLocationRefs = (location) => {
  if (!location || typeof location !== "object") {
    return [];
  }
  const candidates = [
    location.subLocations,
    location.sublocations,
    location.subLocation,
    location.clients,
    location.client,
  ];
  for (let index = 0; index < candidates.length; index += 1) {
    const value = candidates[index];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
};

const selectFilterByChildren = (input, option) => {
  const source = (option && option.props && option.props.children) || "";
  const text = Array.isArray(source) ? source.join(" ") : source.toString();
  return text.toLowerCase().indexOf((input || "").toLowerCase()) >= 0;
};

class SpareStockTaking extends Component {
  state = {
    loading: false,
    listLoading: false,
    saving: false,
    submitting: false,
    createModalVisible: false,
    locations: [],
    subLocations: [],
    spareOptions: [],
    spareOptionMap: {},
    spareSearchFetching: false,
    listData: [],
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
    queueFilters: {
      stockTakingDate: null,
      location: null,
      status: "OPEN",
    },
    queueSummary: {
      draftVouchers: 0,
      submittedVouchers: 0,
      closedVouchers: 0,
      countedSpareParts: 0,
    },
    mode: "list", // list | view
    currentStockTaking: null,
    createForm: {
      stockTakingDate: moment(),
      location: null,
      subLocation: null,
    },
    ignoreRemarkModal: {
      visible: false,
      record: null,
    },
    savModal: {
      visible: false,
      record: null,
    },
    selectedSpareToAdd: null,
    countedQuantity: null,
    clearQuantityOnFocus: false,
    quantityFieldEpoch: 0,
  };

  spareSelectRef = null;

  spareSelectContainerRef = null;

  quantityInputRef = null;

  spareSearchRequestSeq = 0;

  debouncedFetchSpareOptions = createDebounce((searchValue) => {
    this.fetchSpareOptionsFromApi(searchValue);
  }, 350);

  componentDidMount = async () => {
    this.loadMasters();
    await Promise.all([
      this.loadList(1, this.state.pagination.pageSize),
      this.loadQueueSummary(),
    ]);
  };

  componentWillUnmount() {
    if (this.debouncedFetchSpareOptions && this.debouncedFetchSpareOptions.cancel) {
      this.debouncedFetchSpareOptions.cancel();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.authUser !== this.props.authUser) {
      this.loadMasters();
    }
  }

  hasPermission = (permission, moduleName) => {
    const authUser = this.props.authUser || {};
    const roles = authUser.roles || [];
    const effectiveModule = moduleName || this.props.resourceName || "SpareStockTaking";
    const permissions = roles
      .map((role) => (role && role.access) ? role.access : [])
      .flat(1)
      .filter((entry) => entry && entry.name === effectiveModule)
      .map((entry) => entry.permissions || [])
      .flat(1);
    return permissions.indexOf(permission) >= 0;
  };

  canAccessReconciliation = () => {
    return (
      this.hasPermission("r", "SparesReconciliation")
      || this.hasPermission("u", "SparesReconciliation")
      || this.hasPermission("c", "SparesReconciliation")
    );
  };

  getErrorMessage = (error, fallbackMessage) => {
    return (
      (error && error.response && error.response.data && error.response.data.message)
      || fallbackMessage
    );
  };

  tooltipLabel = (label, tip) => (
    <span className="spares-tooltip-label">
      {label}
      <Tooltip title={tip}>
        <Icon type="info-circle" className="spares-tooltip-icon" />
      </Tooltip>
    </span>
  );

  setLoading = (loading) => this.setState({ loading });

  setSpareSelectRef = (node) => {
    this.spareSelectRef = node;
  };

  setSpareSelectContainerRef = (node) => {
    this.spareSelectContainerRef = node;
  };

  setQuantityInputRef = (node) => {
    this.quantityInputRef = node;
  };

  focusSpareSelect = () => {
    const focusAttempt = (attempt = 0) => {
      if (this.spareSelectRef && typeof this.spareSelectRef.focus === "function") {
        this.spareSelectRef.focus();
      }

      // Antd Select search input can remain readonly until dropdown is opened.
      // Force open so user can type immediately after focus.
      if (
        this.spareSelectRef
        && this.spareSelectRef.rcSelect
        && typeof this.spareSelectRef.rcSelect.setOpenState === "function"
      ) {
        this.spareSelectRef.rcSelect.setOpenState(true, true);
      } else {
        const container = this.spareSelectContainerRef;
        const selectionNode = container && container.querySelector
          ? container.querySelector(".ant-select-selection")
          : null;
        if (selectionNode && typeof selectionNode.click === "function") {
          selectionNode.click();
        }
      }

      const container = this.spareSelectContainerRef;
      const inputNode = container && container.querySelector
        ? container.querySelector(".ant-select-search__field")
        : null;

      if (inputNode && typeof inputNode.focus === "function" && !inputNode.readOnly) {
        inputNode.focus();
        return;
      }

      if (attempt < 8) {
        setTimeout(() => focusAttempt(attempt + 1), 35);
      }
    };

    focusAttempt(0);
  };

  focusQuantityInput = () => {
    if (this.quantityInputRef && typeof this.quantityInputRef.focus === "function") {
      this.quantityInputRef.focus();
    }
  };

  loadMasters = () => {
    const authUser = this.props.authUser || {};
    const rawLocations = Array.isArray(authUser.locations) ? authUser.locations : [];
    const rawClients = Array.isArray(authUser.clients) ? authUser.clients : [];

    const clientMap = {};
    rawClients.forEach((client) => {
      const clientId = toId(client && (client._id || client.id));
      if (!clientId) {
        return;
      }
      clientMap[clientId] = {
        _id: clientId,
        name: (client && client.name) || "-",
      };
    });

    const locations = rawLocations
      .map((location) => {
        const locationId = toId(location && (location._id || location.id));
        if (!locationId) {
          return null;
        }

        const rawSubLocations = pickSubLocationRefs(location);
        const subLocationMap = {};
        rawSubLocations.forEach((subLocation) => {
          const subLocationId = toId(subLocation && (subLocation._id || subLocation.id || subLocation));
          if (!subLocationId) {
            return;
          }
          const mapped = typeof subLocation === "object"
            ? subLocation
            : clientMap[subLocationId];
          subLocationMap[subLocationId] = {
            _id: subLocationId,
            name: (mapped && mapped.name) || ((clientMap[subLocationId] && clientMap[subLocationId].name) || "-"),
          };
        });

        return {
          ...location,
          _id: locationId,
          name: (location && location.name) || "-",
          subLocations: Object.values(subLocationMap),
        };
      })
      .filter((item) => !!item)
      .sort((a, b) => a.name.localeCompare(b.name));

    const subLocationMap = {};
    locations.forEach((location) => {
      (location.subLocations || []).forEach((subLocation) => {
        const subLocationId = toId(subLocation && (subLocation._id || subLocation.id));
        if (!subLocationId) {
          return;
        }
        subLocationMap[subLocationId] = {
          _id: subLocationId,
          name: (subLocation && subLocation.name) || "-",
        };
      });
    });
    Object.keys(clientMap).forEach((clientId) => {
      if (!subLocationMap[clientId]) {
        subLocationMap[clientId] = clientMap[clientId];
      }
    });
    const subLocations = Object.values(subLocationMap).sort((a, b) => a.name.localeCompare(b.name));

    this.setState((prevState) => {
      const previousLocationId = toId(prevState.createForm.location);
      const locationExists = previousLocationId
        ? locations.some((location) => toId(location._id) === previousLocationId)
        : true;

      let nextLocation = locationExists ? prevState.createForm.location : null;
      let nextSubLocation = prevState.createForm.subLocation;
      if (!locationExists) {
        nextSubLocation = null;
      } else if (nextSubLocation) {
        const selectedLocation = locations.find((location) => toId(location._id) === previousLocationId);
        const availableSubLocations = selectedLocation && selectedLocation.subLocations && selectedLocation.subLocations.length > 0
          ? selectedLocation.subLocations
          : subLocations;
        const subLocationExists = availableSubLocations.some(
          (subLocation) => toId(subLocation._id) === toId(nextSubLocation),
        );
        if (!subLocationExists) {
          nextSubLocation = null;
        }
      }

      return {
        locations,
        subLocations,
        createForm: {
          ...prevState.createForm,
          location: nextLocation,
          subLocation: nextSubLocation,
        },
      };
    });
  };

  loadList = async (page = 1, pageSize = 10, extra = {}) => {
    this.setState({ listLoading: true });
    try {
      const { queueFilters: queueFiltersPatch = {}, ...queryExtra } = extra || {};
      const offset = (page - 1) * pageSize;
      const queueFilters = {
        ...((this.state && this.state.queueFilters) || {}),
        ...queueFiltersPatch,
      };
      const paramsPayload = {
        limit: `${pageSize}`,
        offset: `${offset}`,
        sortOrder: "-1",
      };

      if (queueFilters && queueFilters.status && queueFilters.status !== "ALL") {
        if (queueFilters.status === "OPEN") {
          paramsPayload.statuses = "DRAFT,SUBMITTED";
        } else {
          paramsPayload.status = queueFilters.status;
        }
      }

      if (queueFilters && queueFilters.location) {
        paramsPayload.location = queueFilters.location.toString();
      }

      if (queueFilters && queueFilters.stockTakingDate) {
        paramsPayload.stockTakingDate = moment(queueFilters.stockTakingDate).format("YYYY-MM-DD");
      }

      const params = new URLSearchParams({
        ...paramsPayload,
        ...queryExtra,
      });
      const response = await axios.get(`/spare-stock-taking?${params.toString()}`);
      const payload = (response && response.data) || {};
      this.setState({
        listData: payload.data || [],
        pagination: {
          current: page,
          pageSize,
          total: payload.count || 0,
        },
      });
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to load Spare Stock Taking list"));
    } finally {
      this.setState({ listLoading: false });
    }
  };

  loadQueueSummary = async () => {
    try {
      const response = await axios.get("/spare-stock-taking/summary");
      const payload = (response && response.data) || {};
      this.setState({
        queueSummary: {
          draftVouchers: Number(payload.draftVouchers || 0),
          submittedVouchers: Number(payload.submittedVouchers || 0),
          closedVouchers: Number(payload.closedVouchers || 0),
          countedSpareParts: Number(payload.countedSpareParts || 0),
        },
      });
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to load Spare Stock Taking summary"));
    }
  };

  updateQueueFilters = async (patch = {}) => {
    await new Promise((resolve) => this.setState((prevState) => ({
      queueFilters: {
        ...prevState.queueFilters,
        ...patch,
      },
      pagination: {
        ...prevState.pagination,
        current: 1,
      },
    }), resolve));
    await this.loadList(1, this.state.pagination.pageSize);
  };

  handleQueueDateFilterChange = async (value) => {
    await this.updateQueueFilters({ stockTakingDate: value || null });
  };

  handleQueueLocationFilterChange = async (value) => {
    await this.updateQueueFilters({ location: value || null });
  };

  handleQueueStatusFilterChange = async (value) => {
    await this.updateQueueFilters({ status: value || "OPEN" });
  };

  fetchSpareOptionsFromApi = async (searchValue) => {
    const requestSeq = ++this.spareSearchRequestSeq;
    this.setState({ spareSearchFetching: true });
    try {
      const response = await axios.get(
        `/spare-part/search-spares?searchValue=${encodeURIComponent(searchValue)}`,
      );
      if (requestSeq !== this.spareSearchRequestSeq) {
        return;
      }
      const options = (response && response.data) || [];
      this.setState((prevState) => {
        const nextMap = { ...prevState.spareOptionMap };
        options.forEach((item) => {
          if (item && item._id) {
            nextMap[item._id] = item;
          }
        });
        return {
          spareOptions: options,
          spareOptionMap: nextMap,
          spareSearchFetching: false,
        };
      });
    } catch (error) {
      if (requestSeq !== this.spareSearchRequestSeq) {
        return;
      }
      this.setState({ spareSearchFetching: false, spareOptions: [] });
    }
  };

  fetchSpareOptions = (searchValue) => {
    const normalized = (searchValue || "").toString().trim();
    if (!normalized || normalized.length < 2) {
      this.spareSearchRequestSeq += 1;
      if (this.debouncedFetchSpareOptions && this.debouncedFetchSpareOptions.cancel) {
        this.debouncedFetchSpareOptions.cancel();
      }
      this.setState({ spareOptions: [], spareSearchFetching: false });
      return;
    }
    this.debouncedFetchSpareOptions(normalized);
  };

  handleSpareSelectionChange = (value) => {
    this.setState(
      (prevState) => ({
        selectedSpareToAdd: value || null,
        countedQuantity: null,
        clearQuantityOnFocus: false,
        quantityFieldEpoch: (prevState.quantityFieldEpoch || 0) + 1,
      }),
      () => {
        if (value) {
          this.focusQuantityInput();
        }
      },
    );
  };

  handleQuantityKeyDown = (event) => {
    if (event && event.key === "Enter") {
      event.preventDefault();
      this.addRecordToOpenStockTaking();
    }
  };

  handleQuantityFocus = () => {
    if (this.state.clearQuantityOnFocus) {
      this.setState({
        countedQuantity: null,
        clearQuantityOnFocus: false,
      });
    }
  };

  openCreateModal = () => {
    this.setState({
      createModalVisible: true,
      createForm: {
        stockTakingDate: moment(),
        location: null,
        subLocation: null,
      },
    });
  };

  closeCreateModal = () => {
    this.setState({ createModalVisible: false });
  };

  openIgnoreRemarkModal = (record) => {
    this.setState({
      ignoreRemarkModal: {
        visible: true,
        record: record || null,
      },
    });
  };

  closeIgnoreRemarkModal = () => {
    this.setState({
      ignoreRemarkModal: {
        visible: false,
        record: null,
      },
    });
  };

  openSavModal = (record) => {
    this.setState({
      savModal: {
        visible: true,
        record: record || null,
      },
    });
  };

  closeSavModal = () => {
    this.setState({
      savModal: {
        visible: false,
        record: null,
      },
    });
  };

  formatCurrency = (value) => {
    const amount = toNumber(value);
    return `Rs ${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  getSavDetailRows = (record) => {
    if (!record) {
      return [];
    }

    const rowKey = (record && (record._id || record.key)) ? (record._id || record.key) : "sav";
    const rowSavEntries = Array.isArray(record && record.savEntries) ? record.savEntries : [];

    if (rowSavEntries.length > 0) {
      return rowSavEntries.map((entry, index) => {
        const hasOriginalQuantity = entry && entry.originalQuantity !== undefined && entry.originalQuantity !== null;
        const originalQuantity = hasOriginalQuantity ? toNumber(entry.originalQuantity) : null;
        const adjustmentQuantity = toNumber(entry && entry.adjustmentQuantity);
        const resultingQuantity = hasOriginalQuantity
          ? toNumber(Number(originalQuantity + adjustmentQuantity).toFixed(6))
          : null;
        const rate = toNumber(entry && entry.rate);
        const value = toNumber(entry && entry.value) || Math.abs(adjustmentQuantity * rate);
        const subLocationId = entry && entry.subLocation ? entry.subLocation : null;

        return {
          key: (entry && entry._id) || `${rowKey}-sav-${index}`,
          voucherNo: (entry && entry.voucherNo) || "-",
          transactionType: (entry && entry.transactionType) || "",
          subLocationName: subLocationId ? this.getSubLocationNameById(subLocationId) : "No Sub-location",
          originalQuantity,
          adjustmentQuantity,
          resultingQuantity,
          rate,
          value,
          remarks: (entry && entry.remarks) || "",
          createdAt: (entry && entry.createdAt) || null,
        };
      });
    }

    const voucherNos = (Array.isArray(record && record.savVoucherNos) && record.savVoucherNos.length > 0)
      ? record.savVoucherNos
      : ((record && record.savVoucherNo) ? [record.savVoucherNo] : []);

    if (!voucherNos.length) {
      return [];
    }

    const qty = toNumber(record.adjustmentQuantity);
    const rate = toNumber(record.adjustmentRate);
    const value = Math.abs(qty * rate);
    const fallbackFootprint = record && record.subLocation
      ? this.getSubLocationNameById(record.subLocation)
      : "No Sub-location";

    return voucherNos.map((voucherNo, index) => ({
      key: `${rowKey}-${voucherNo}-${index}`,
      voucherNo: voucherNo || "-",
      transactionType: qty >= 0 ? "ADJUSTMENT_INWARD" : "ADJUSTMENT_OUTWARD",
      subLocationName: fallbackFootprint || "-",
      originalQuantity: null,
      adjustmentQuantity: index === 0 ? qty : 0,
      resultingQuantity: null,
      rate: index === 0 ? rate : 0,
      value: index === 0 ? value : 0,
      remarks: (record && record.remarks) || "",
      createdAt: (record && record.processedAt) || null,
    }));
  };

  openView = async (id) => {
    if (!id) {
      return;
    }
    this.setLoading(true);
    try {
      const response = await axios.get(`/spare-stock-taking/${id}`);
      const data = response && response.data ? response.data : null;
      this.setState({
        mode: "view",
        currentStockTaking: data,
        selectedSpareToAdd: null,
        countedQuantity: null,
        clearQuantityOnFocus: false,
      });
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to load Spare Stock Taking"));
    } finally {
      this.setLoading(false);
    }
  };

  backToQueue = async () => {
    this.setState({
      mode: "list",
      currentStockTaking: null,
      selectedSpareToAdd: null,
      countedQuantity: null,
      clearQuantityOnFocus: false,
    });
    await Promise.all([
      this.loadList(this.state.pagination.current, this.state.pagination.pageSize),
      this.loadQueueSummary(),
    ]);
  };

  canEditStockTaking = (stockTaking) => {
    return !!(stockTaking && stockTaking.status === "DRAFT");
  };

  createStockTaking = async () => {
    if (!this.hasPermission("c")) {
      message.error("You do not have create permission.");
      return;
    }

    const { createForm, pagination } = this.state;
    if (!createForm.stockTakingDate || !createForm.location) {
      message.error("Date and location are required");
      return;
    }

    this.setState({ saving: true });
    try {
      const payload = {
        stockTakingDate: moment(createForm.stockTakingDate).toISOString(),
        location: createForm.location,
        subLocation: createForm.subLocation || null,
      };
      const response = await axios.post("/spare-stock-taking/generate", payload);
      const created = response && response.data ? response.data : null;
      if (!created || !created._id) {
        throw new Error("SST was created but response is invalid");
      }

      message.success(`Voucher ${created.voucherNo} created`);
      this.setState({ createModalVisible: false });
      await Promise.all([
        this.loadList(pagination.current, pagination.pageSize),
        this.loadQueueSummary(),
      ]);
      await this.openView(created._id);
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to create Spare Stock Taking"));
    } finally {
      this.setState({ saving: false });
    }
  };

  addRecordToOpenStockTaking = async () => {
    const { currentStockTaking, selectedSpareToAdd, countedQuantity, pagination } = this.state;
    let shouldRefocusSpare = false;

    if (!currentStockTaking || !currentStockTaking._id) {
      message.error("No active Spare Stock Taking");
      return;
    }
    if (!this.canEditStockTaking(currentStockTaking)) {
      message.error("Only DRAFT SST can be modified");
      return;
    }
    if (!this.hasPermission("u") && !this.hasPermission("c")) {
      message.error("You do not have permission to add records.");
      return;
    }
    if (!selectedSpareToAdd) {
      message.error("Please select a spare part");
      return;
    }

    const quantity = Number(countedQuantity);
    if (!Number.isFinite(quantity) || quantity < 0) {
      message.error("Please enter a valid counted quantity");
      return;
    }

    this.setLoading(true);
    try {
      const response = await axios.post(`/spare-stock-taking/${currentStockTaking._id}/records`, {
        sparePart: selectedSpareToAdd,
        countedQuantity: quantity,
      });
      message.success("Spare line added");
      this.setState((prevState) => ({
          currentStockTaking: response && response.data ? response.data : currentStockTaking,
          selectedSpareToAdd: null,
          countedQuantity: null,
          clearQuantityOnFocus: false,
          quantityFieldEpoch: (prevState.quantityFieldEpoch || 0) + 1,
        }));
      await Promise.all([
        this.loadList(pagination.current, pagination.pageSize),
        this.loadQueueSummary(),
      ]);
      shouldRefocusSpare = true;
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to add spare entry"));
    } finally {
      this.setLoading(false);
      if (shouldRefocusSpare) {
        setTimeout(() => this.focusSpareSelect(), 40);
      }
    }
  };

  removeRecordFromOpenStockTaking = async (record) => {
    const { currentStockTaking, pagination } = this.state;
    if (!currentStockTaking || !currentStockTaking._id || !record || !record._id) {
      return;
    }
    if (!this.canEditStockTaking(currentStockTaking)) {
      message.error("Only DRAFT SST can be modified");
      return;
    }
    if (!this.hasPermission("u") && !this.hasPermission("d")) {
      message.error("You do not have permission to remove entries.");
      return;
    }

    this.setLoading(true);
    try {
      const response = await axios.delete(
        `/spare-stock-taking/${currentStockTaking._id}/records/${record._id}`,
      );
      message.success("Spare line removed");
      this.setState({
        currentStockTaking: response && response.data ? response.data : currentStockTaking,
      });
      await Promise.all([
        this.loadList(pagination.current, pagination.pageSize),
        this.loadQueueSummary(),
      ]);
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to remove spare entry"));
    } finally {
      this.setLoading(false);
    }
  };

  submitStockTaking = async () => {
    const { currentStockTaking, pagination } = this.state;
    if (!currentStockTaking || !currentStockTaking._id) {
      return;
    }
    if (currentStockTaking.status !== "DRAFT") {
      message.error("Only DRAFT SST can be submitted");
      return;
    }
    if (!this.hasPermission("u") && !this.hasPermission("c")) {
      message.error("You do not have permission to submit SST.");
      return;
    }

    this.setState({ submitting: true });
    try {
      const response = await axios.put(`/spare-stock-taking/${currentStockTaking._id}/submit`, {});
      const updated = response && response.data ? response.data : currentStockTaking;
      message.success("SST submitted");
      this.setState({ currentStockTaking: updated });
      await Promise.all([
        this.loadList(pagination.current, pagination.pageSize),
        this.loadQueueSummary(),
      ]);
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to submit SST"));
    } finally {
      this.setState({ submitting: false });
    }
  };

  goToReconcile = (id) => {
    if (!id) {
      return;
    }
    if (!this.canAccessReconciliation()) {
      message.error("You do not have permission to access Spares Reconciliation.");
      return;
    }
    this.props.history.push(`/spares-reconciliation?sstId=${encodeURIComponent(id)}`);
  };

  openAdjustmentVoucherReport = (stockTaking = null) => {
    const params = new URLSearchParams();
    if (stockTaking && stockTaking.stockTakingDate) {
      const reportDate = moment(stockTaking.stockTakingDate);
      if (reportDate.isValid()) {
        const normalized = reportDate.format("YYYY-MM-DD");
        params.set("fromDate", normalized);
        params.set("toDate", normalized);
      }
    }
    if (stockTaking && stockTaking.filters && stockTaking.filters.location) {
      params.set("location", stockTaking.filters.location.toString());
    }
    if (stockTaking && stockTaking.filters && stockTaking.filters.subLocation) {
      params.set("subLocation", stockTaking.filters.subLocation.toString());
    }
    const query = params.toString();
    this.props.history.push(`/spare-adjustment-vouchers${query ? `?${query}` : ""}`);
  };

  deleteStockTaking = async (stockTaking) => {
    if (!stockTaking || !stockTaking._id) {
      return;
    }
    if (stockTaking.status === "CLOSED") {
      message.error("Closed SST cannot be deleted");
      return;
    }
    if (!this.hasPermission("d")) {
      message.error("You do not have delete permission.");
      return;
    }

    this.setState({ listLoading: true });
    try {
      await axios.delete(`/spare-stock-taking/${stockTaking._id}`);
      message.success("Spare Stock Taking deleted");
      if (
        this.state.currentStockTaking
        && this.state.currentStockTaking._id
        && this.state.currentStockTaking._id.toString() === stockTaking._id.toString()
      ) {
        this.setState({
          mode: "list",
          currentStockTaking: null,
          selectedSpareToAdd: null,
          countedQuantity: null,
        });
      }
      await Promise.all([
        this.loadList(this.state.pagination.current, this.state.pagination.pageSize),
        this.loadQueueSummary(),
      ]);
    } catch (error) {
      message.error(this.getErrorMessage(error, "Failed to delete Spare Stock Taking"));
      this.setState({ listLoading: false });
    }
  };

  getStatusTag = (status) => {
    if (status === "CLOSED") {
      return <Tag className="spares-status-tag spares-status-closed">Closed</Tag>;
    }
    if (status === "SUBMITTED") {
      return <Tag className="spares-status-tag spares-status-submitted">Submitted</Tag>;
    }
    return <Tag className="spares-status-tag spares-status-draft">Draft</Tag>;
  };

  getLocationNameById = (locationId) => {
    const { locations } = this.state;
    if (!locationId) {
      return "-";
    }
    const found = (locations || []).find((item) => (
      item && item._id && item._id.toString() === locationId.toString()
    ));
    return (found && found.name) || "-";
  };

  getSubLocationNameById = (subLocationId) => {
    const { subLocations } = this.state;
    if (!subLocationId) {
      return "-";
    }
    const found = (subLocations || []).find((item) => (
      item && item._id && item._id.toString() === subLocationId.toString()
    ));
    return (found && found.name) || "-";
  };

  getQueueColumns = () => {
    const canView = this.hasPermission("r") || this.hasPermission("u");
    const canDelete = this.hasPermission("d");
    const canReconcile = this.canAccessReconciliation();
    const openVoucher = (record) => {
      if (!record || !record._id) {
        return;
      }
      if (record.status === "CLOSED") {
        this.goToReconcile(record._id);
        return;
      }
      this.openView(record._id);
    };

    return [
      {
        title: this.tooltipLabel("Voucher", "Unique stock-taking voucher number"),
        dataIndex: "voucherNo",
        key: "voucherNo",
        render: (value, record) => (
          <button type="button" className="spares-link-button" onClick={() => openVoucher(record)}>
            {value}
          </button>
        ),
      },
      {
        title: this.tooltipLabel("Date", "Date selected while creating SST"),
        dataIndex: "stockTakingDate",
        key: "stockTakingDate",
        render: (value) => (value ? moment(value).format("DD MMM YYYY") : "-"),
      },
      {
        title: this.tooltipLabel("Location", "Primary location of the SST"),
        key: "location",
        render: (_, record) => (
          (record && record.filters && record.filters.location && record.filters.location.name) || "-"
        ),
      },
      {
        title: this.tooltipLabel("Sub Location", "Optional sub location, '-' if not selected"),
        key: "subLocation",
        render: (_, record) => (
          (record && record.filters && record.filters.subLocation && record.filters.subLocation.name) || "-"
        ),
      },
      {
        title: this.tooltipLabel("Status", "DRAFT, SUBMITTED or CLOSED"),
        dataIndex: "status",
        key: "status",
        render: (value) => this.getStatusTag(value),
      },
      {
        title: this.tooltipLabel("Spare Parts", "Total spare parts added in this SST"),
        dataIndex: "totalRecords",
        key: "totalRecords",
        align: "right",
        render: (value) => Number(value || 0),
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => {
          if (record.status === "SUBMITTED") {
            return (
              <div className="spares-row-actions">
                <Button
                  size="small"
                  className="spares-btn spares-btn-reconcile"
                  disabled={!canReconcile}
                  onClick={() => this.goToReconcile(record._id)}
                >
                  Reconcile
                </Button>
              </div>
            );
          }

          return (
            <div className="spares-row-actions">
              <Button
                size="small"
                className="spares-btn spares-btn-open"
                disabled={record.status === "CLOSED" ? !canReconcile : !canView}
                onClick={() => openVoucher(record)}
              >
                {record.status === "CLOSED" ? "View" : "Open"}
              </Button>
              {canDelete && (
                <Popconfirm
                  title="Delete this SST?"
                  onConfirm={() => this.deleteStockTaking(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    size="small"
                    className="spares-btn spares-btn-remove"
                    disabled={record.status === "CLOSED"}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </div>
          );
        },
      },
    ];
  };

  getWorkspaceColumns = () => {
    const { currentStockTaking } = this.state;
    const canEdit = this.canEditStockTaking(currentStockTaking);
    const canModify = canEdit && (this.hasPermission("u") || this.hasPermission("d"));

    return [
      {
        title: this.tooltipLabel("Spare Code", "Spare code from master"),
        dataIndex: "spareCode",
        key: "spareCode",
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Spare Name", "Spare name from master"),
        dataIndex: "spareName",
        key: "spareName",
      },
      {
        title: this.tooltipLabel("Category", "Spare type/category"),
        dataIndex: "spareTypeName",
        key: "spareTypeName",
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("UOM", "Unit of measurement"),
        dataIndex: "unitName",
        key: "unitName",
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Quantity Found", "Entered counted quantity"),
        dataIndex: "countedQuantity",
        key: "countedQuantity",
        align: "right",
        render: (value) => Number(value || 0).toLocaleString(),
      },
      {
        title: "Action",
        key: "action",
        render: (_, record) => {
          if (!canEdit) {
            const action = ((record && record.action) || "").toString().toUpperCase();
            if (action === "IGNORE") {
              return (
                <div className="spares-row-actions">
                  <Tag color="green">Reviewed</Tag>
                  <button
                    type="button"
                    className="spares-link-button spares-action-link-ignore"
                    onClick={() => this.openIgnoreRemarkModal(record)}
                  >
                    Ignored
                  </button>
                </div>
              );
            }
            if (action === "ADJUSTMENT") {
              const rowSavEntries = Array.isArray(record && record.savEntries) ? record.savEntries : [];
              const savVoucherNos = (Array.isArray(record && record.savVoucherNos) && record.savVoucherNos.length > 0)
                ? record.savVoucherNos
                : ((record && record.savVoucherNo) ? [record.savVoucherNo] : []);
              const savCount = rowSavEntries.length || savVoucherNos.length;
              const savLabel = savCount <= 1
                ? (savVoucherNos[0] || (rowSavEntries[0] && rowSavEntries[0].voucherNo) || "Adjusted")
                : `${savCount} SAV`;

              return (
                <div className="spares-row-actions">
                  <Tag color="green">Reviewed</Tag>
                  {savCount > 0 ? (
                    <button
                      type="button"
                      className="spares-link-button"
                      onClick={() => this.openSavModal(record)}
                    >
                      {savLabel}
                    </button>
                  ) : (
                    <Tag color="blue">Adjusted</Tag>
                  )}
                </div>
              );
            }
            return <Tag color="blue">Locked</Tag>;
          }
          return (
            <Button
              size="small"
              className="spares-btn spares-btn-remove"
              disabled={!canModify}
              onClick={() => this.removeRecordFromOpenStockTaking(record)}
            >
              Remove
            </Button>
          );
        },
      },
    ];
  };

  renderListPage = () => {
    const {
      listData,
      pagination,
      listLoading,
      queueFilters,
      queueSummary,
      locations,
    } = this.state;
    const canCreate = this.hasPermission("c");
    const draftCount = Number((queueSummary && queueSummary.draftVouchers) || 0);
    const submittedCount = Number((queueSummary && queueSummary.submittedVouchers) || 0);
    const closedCount = Number((queueSummary && queueSummary.closedVouchers) || 0);
    const totalLines = Number((queueSummary && queueSummary.countedSpareParts) || 0);

    return (
      <div className="spares-page">
        <div className="spares-hero">
          <div>
            <h3>Spares stock taking with guided voucher creation and counted-line capture.</h3>
            <p>
              Create a voucher, record physically counted spare lines, and move submitted vouchers to reconciliation.
            </p>
          </div>
          <div className="spares-row-actions">
            <Button
              size="large"
              className="spares-btn"
              onClick={() => this.openAdjustmentVoucherReport()}
            >
              Spare Adjustment Vouchers
            </Button>
            <Button
              type="primary"
              size="large"
              icon="plus"
              className="spares-btn spares-btn-primary spares-btn-lg"
              disabled={!canCreate}
              onClick={this.openCreateModal}
            >
              Create Stock Taking Voucher
            </Button>
          </div>
        </div>

        <Row gutter={16} className="spares-stat-row">
          <Col xs={24} md={12} xl={6}>
            <Card className="spares-stat-card">
              <Statistic
                title={this.tooltipLabel("Draft Vouchers", "SST vouchers in DRAFT status")}
                value={draftCount}
                prefix={<Icon type="edit" />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card className="spares-stat-card">
              <Statistic
                title={this.tooltipLabel("Submitted Vouchers", "SST vouchers in SUBMITTED status")}
                value={submittedCount}
                prefix={<Icon type="check-circle" />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card className="spares-stat-card">
              <Statistic
                title={this.tooltipLabel("Closed Vouchers", "SST vouchers in CLOSED status")}
                value={closedCount}
                prefix={<Icon type="lock" />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card className="spares-stat-card spares-stat-card-accent">
              <Statistic
                title={this.tooltipLabel("Counted Spare Parts", "Total counted spare-part entries across SST vouchers")}
                value={totalLines}
                prefix={<Icon type="database" />}
              />
            </Card>
          </Col>
        </Row>

        <Card
          className="spares-panel-card"
          title={this.tooltipLabel("Voucher Queue", "All created SST vouchers")}
          extra={(
            <div className="spares-queue-filter-bar">
              <DatePicker
                allowClear
                value={queueFilters && queueFilters.stockTakingDate ? moment(queueFilters.stockTakingDate) : null}
                placeholder="Date"
                onChange={this.handleQueueDateFilterChange}
                className="spares-queue-filter-date"
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={selectFilterByChildren}
                value={queueFilters && queueFilters.location ? queueFilters.location : undefined}
                placeholder="Location"
                className="spares-queue-filter-select"
                onChange={this.handleQueueLocationFilterChange}
              >
                {(locations || []).map((item) => (
                  <Select.Option key={item._id} value={item._id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                value={(queueFilters && queueFilters.status) || "OPEN"}
                className="spares-queue-filter-select"
                onChange={this.handleQueueStatusFilterChange}
              >
                <Select.Option value="OPEN">Open</Select.Option>
                <Select.Option value="DRAFT">Draft</Select.Option>
                <Select.Option value="SUBMITTED">Submitted</Select.Option>
                <Select.Option value="CLOSED">Closed</Select.Option>
                <Select.Option value="ALL">All</Select.Option>
              </Select>
              <Tag color="blue">{listData.length} total</Tag>
            </div>
          )}
        >
          <Table
            loading={listLoading}
            dataSource={listData}
            columns={this.getQueueColumns()}
            rowKey="_id"
            pagination={pagination}
            onChange={(nextPagination) => this.loadList(nextPagination.current, nextPagination.pageSize)}
            scroll={{ x: true }}
          />
        </Card>
      </div>
    );
  };

  renderWorkspacePage = () => {
    const {
      loading,
      submitting,
      currentStockTaking,
      locations,
      subLocations,
      selectedSpareToAdd,
      countedQuantity,
      quantityFieldEpoch,
      spareOptions,
      spareSearchFetching,
      spareOptionMap,
    } = this.state;

    if (!currentStockTaking) {
      return (
        <Card className="spares-panel-card">
          <Button className="spares-btn" onClick={this.backToQueue}>Back to Queue</Button>
        </Card>
      );
    }

    const canEdit = this.canEditStockTaking(currentStockTaking);
    const canAdd = canEdit && (this.hasPermission("u") || this.hasPermission("c"));
    const canSubmit = canEdit && (this.hasPermission("u") || this.hasPermission("c"));
    const locationId = currentStockTaking.filters && currentStockTaking.filters.location;
    const subLocationId = currentStockTaking.filters && currentStockTaking.filters.subLocation;
    const selectedLocation = (locations || []).find((loc) => (
      loc && loc._id && locationId && loc._id.toString() === locationId.toString()
    ));
    const availableSubLocations = selectedLocation && selectedLocation.subLocations && selectedLocation.subLocations.length > 0
      ? selectedLocation.subLocations
      : (subLocations || []);
    const locationLabel = (() => {
      const lName = this.getLocationNameById(locationId);
      const sName = this.getSubLocationNameById(subLocationId);
      return sName && sName !== "-" ? `${lName} / ${sName}` : lName;
    })();

    const rows = (currentStockTaking.records || []).map((row) => {
      const spareId = row && row.spare && row.spare._id
        ? row.spare._id
        : (row && row.spare ? row.spare : null);
      const resolvedSpare = spareId ? spareOptionMap[spareId.toString()] : null;

      return {
        ...row,
        key: row._id,
        spareCode: (row && row.spareCode)
          || (row && row.code)
          || (row && row.spare && row.spare.code)
          || (row && row.sparePart && row.sparePart.code)
          || (resolvedSpare && resolvedSpare.code)
          || "",
        spareName: (row && row.spareName)
          || (row && row.name)
          || (row && row.spare && row.spare.name)
          || (row && row.sparePart && row.sparePart.name)
          || (resolvedSpare && resolvedSpare.name)
          || "-",
        spareTypeName: (row && row.spareTypeName)
          || (row && row.spareType && row.spareType.name)
          || (resolvedSpare && resolvedSpare.spareTypeName)
          || (resolvedSpare && resolvedSpare.type && resolvedSpare.type.name)
          || "-",
        unitName: (row && row.unitName)
          || (row && row.unit)
          || (row && row.unitOfMeasurement && row.unitOfMeasurement.name)
          || (resolvedSpare && resolvedSpare.unit)
          || "-",
      };
    });
    const selectedSpare = selectedSpareToAdd ? spareOptionMap[selectedSpareToAdd] : null;

    return (
      <div className="spares-page">
        <Card
          className="spares-panel-card"
          title={this.tooltipLabel(`Voucher Workspace • ${currentStockTaking.voucherNo || "-"}`, "View and update SST in DRAFT")}
          extra={(
            <div className="spares-workspace-actions">
              <Button className="spares-btn" onClick={this.backToQueue}>
                <Icon type="arrow-left" />
                Back to Queue
              </Button>
              <Button className="spares-btn" onClick={() => this.openAdjustmentVoucherReport(currentStockTaking)}>
                Spare Adjustment Vouchers
              </Button>
              {this.getStatusTag(currentStockTaking.status)}
            </div>
          )}
        >
          {currentStockTaking.status === "SUBMITTED" && (
            <Alert
              className="spares-alert"
              type="success"
              message="This report is submitted and ready for reconciliation."
              showIcon
            />
          )}
          {currentStockTaking.status === "CLOSED" && (
            <Alert
              className="spares-alert"
              type="info"
              message="This report is closed and locked."
              showIcon
            />
          )}

          <div className="spares-form-grid">
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Date", "SST date")}</span>
              <DatePicker value={moment(currentStockTaking.stockTakingDate)} disabled style={{ width: "100%" }} />
            </div>
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Location", "Selected location for this SST")}</span>
              <Select value={locationId || undefined} disabled>
                {(locations || []).map((item) => (
                  <Select.Option key={item._id} value={item._id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="spares-form-field">
              <span>{this.tooltipLabel("Sub Location (Optional)", "Optional sub location, '-' when not selected")}</span>
              <Select value={subLocationId || undefined} disabled allowClear>
                {(availableSubLocations || []).map((item) => (
                  <Select.Option key={item._id} value={item._id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {canEdit && (
            <div className="spares-line-builder">
              <div className="spares-line-builder-head">
                <div>
                  <h4>{this.tooltipLabel("Add Counted Spare", "Add spare lines while SST is in DRAFT")}</h4>
                  <p>Select spare from master and enter counted quantity.</p>
                </div>
              <Button
                type="primary"
                className="spares-btn spares-btn-primary"
                disabled={!canAdd}
                onClick={this.addRecordToOpenStockTaking}
              >
                Add Spare
              </Button>
              </div>

              <div className="spares-line-grid">
                <div className="spares-form-field" ref={this.setSpareSelectContainerRef}>
                  <span>{this.tooltipLabel("Spare", "Search and select spare part")}</span>
                  <Select
                    ref={this.setSpareSelectRef}
                    showSearch
                    allowClear
                    value={selectedSpareToAdd || undefined}
                    placeholder="Select spare from master"
                    filterOption={false}
                    notFoundContent={spareSearchFetching ? <Icon type="loading" /> : null}
                    disabled={!canAdd}
                    onSearch={this.fetchSpareOptions}
                    onChange={this.handleSpareSelectionChange}
                  >
                    {(spareOptions || []).map((item) => (
                      <Select.Option key={item._id} value={item._id}>
                        {item.code ? `${item.code} - ${item.name}` : item.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Unit of Measurement", "Unit from selected spare")}</span>
                  <div className="spares-readonly-note">
                    {selectedSpare
                      ? (selectedSpare.unit || "-")
                      : "Auto from selected spare"}
                  </div>
                </div>

                <div className="spares-form-field">
                  <span>{this.tooltipLabel("Quantity Found", "Enter counted quantity")}</span>
                  <InputNumber
                    key={`sst-qty-${quantityFieldEpoch}`}
                    ref={this.setQuantityInputRef}
                    style={{ width: "100%" }}
                    min={0}
                    value={countedQuantity}
                    disabled={!canAdd}
                    onChange={(value) => this.setState({ countedQuantity: value })}
                    onFocus={this.handleQuantityFocus}
                    onKeyDown={this.handleQuantityKeyDown}
                  />
                </div>
              </div>
            </div>
          )}

          <Table
            className="spares-lines-table"
            loading={loading}
            dataSource={rows}
            columns={this.getWorkspaceColumns()}
            rowKey="key"
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
          />

          <div className="spares-footer-actions">
            <div className="spares-footer-note">
              {rows.length} spares captured for {locationLabel}
            </div>
            <div className="spares-row-actions">
              {canSubmit && (
                <Button
                  type="primary"
                  className="spares-btn spares-btn-primary"
                  loading={submitting}
                  onClick={this.submitStockTaking}
                >
                  Submit Report
                </Button>
              )}
              {currentStockTaking.status === "SUBMITTED" && (
                <Button
                  type="primary"
                  ghost
                  className="spares-btn spares-btn-ghost"
                  onClick={() => this.goToReconcile(currentStockTaking._id)}
                >
                  Open Reconciliation
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderCreateModal = () => {
    const {
      createModalVisible,
      createForm,
      saving,
      locations,
      subLocations,
    } = this.state;
    const selectedLocation = (locations || []).find((item) => (
      item && item._id && createForm.location && item._id.toString() === createForm.location.toString()
    ));
    const createSubLocations = selectedLocation && selectedLocation.subLocations && selectedLocation.subLocations.length > 0
      ? selectedLocation.subLocations
      : (subLocations || []);

    return (
      <Modal
        title={this.tooltipLabel("Create Spares Stock Taking Voucher", "Create a new SST for date/location")}
        visible={createModalVisible}
        onOk={this.createStockTaking}
        okText="Create Voucher"
        okButtonProps={{ className: "spares-btn spares-btn-primary" }}
        onCancel={this.closeCreateModal}
        confirmLoading={saving}
      >
        <div className="spares-form-grid">
          <div className="spares-form-field">
            <span>{this.tooltipLabel("Date", "Stock taking date")}</span>
            <DatePicker
              value={createForm.stockTakingDate}
              style={{ width: "100%" }}
              onChange={(value) => this.setState((prevState) => ({
                createForm: {
                  ...prevState.createForm,
                  stockTakingDate: value,
                },
              }))}
            />
          </div>
          <div className="spares-form-field">
            <span>{this.tooltipLabel("Location", "Primary location for this SST")}</span>
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={selectFilterByChildren}
              value={createForm.location || undefined}
              placeholder="Choose location"
              onChange={(value) => this.setState((prevState) => ({
                createForm: {
                  ...prevState.createForm,
                  location: value || null,
                  subLocation: null,
                },
              }))}
            >
              {(locations || []).map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="spares-form-field">
            <span>{this.tooltipLabel("Sub Location (Optional)", "Optional sub location. If none selected, '-' will be shown")}</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={selectFilterByChildren}
              value={createForm.subLocation || undefined}
              placeholder="Choose sub location"
              onChange={(value) => this.setState((prevState) => ({
                createForm: {
                  ...prevState.createForm,
                  subLocation: value || null,
                },
              }))}
            >
              {(createSubLocations || []).map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    );
  };

  renderIgnoreRemarkModal = () => {
    const { ignoreRemarkModal } = this.state;
    const record = ignoreRemarkModal && ignoreRemarkModal.record ? ignoreRemarkModal.record : null;
    const spareLabel = record
      ? `${record.spareCode || "-"} • ${record.spareName || "-"}`
      : "-";
    const remark = record && record.remarks ? record.remarks : "-";

    return (
      <Modal
        title={this.tooltipLabel("Ignore Remark", "Remark captured when this record was marked as ignored")}
        visible={!!(ignoreRemarkModal && ignoreRemarkModal.visible)}
        onOk={this.closeIgnoreRemarkModal}
        onCancel={this.closeIgnoreRemarkModal}
        okText="Close"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <div className="spares-form-field">
          <span>{this.tooltipLabel("Spare", "Ignored spare record")}</span>
          <div className="spares-readonly-note">{spareLabel}</div>
        </div>
        <div className="spares-form-field" style={{ marginTop: 12 }}>
          <span>{this.tooltipLabel("Remark", "Reason entered during ignore action")}</span>
          <div className="spares-readonly-note">{remark}</div>
        </div>
      </Modal>
    );
  };

  renderSavModal = () => {
    const { savModal } = this.state;
    const record = savModal && savModal.record ? savModal.record : null;
    const savRows = this.getSavDetailRows(record);
    const totalSav = record
      ? (
        toNumber(record.savCount)
        || (Array.isArray(record.savVoucherNos) ? record.savVoucherNos.length : 0)
        || (record.savVoucherNo ? 1 : 0)
      )
      : 0;
    const savColumns = [
      {
        title: this.tooltipLabel("SAV Voucher", "Spares Adjustment Voucher number"),
        dataIndex: "voucherNo",
        key: "voucherNo",
        width: 140,
      },
      {
        title: this.tooltipLabel("Type", "Adjustment direction"),
        dataIndex: "transactionType",
        key: "transactionType",
        width: 150,
        render: (value) => {
          const type = value ? value.toString().toUpperCase() : "";
          if (type.indexOf("OUTWARD") >= 0) {
            return <Tag color="orange">OUTWARD</Tag>;
          }
          if (type.indexOf("INWARD") >= 0) {
            return <Tag color="green">INWARD</Tag>;
          }
          return <Tag>-</Tag>;
        },
      },
      {
        title: this.tooltipLabel("Sub-location", "Sub-location context for SAV"),
        dataIndex: "subLocationName",
        key: "subLocationName",
        width: 200,
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Original Qty", "Quantity before this adjustment"),
        dataIndex: "originalQuantity",
        key: "originalQuantity",
        width: 120,
        align: "right",
        render: (value) => {
          if (value === null || value === undefined) {
            return "-";
          }
          return toNumber(value).toLocaleString();
        },
      },
      {
        title: this.tooltipLabel("Adjustment Qty", "Adjusted quantity in this SAV row"),
        dataIndex: "adjustmentQuantity",
        key: "adjustmentQuantity",
        width: 130,
        align: "right",
        render: (value) => {
          const qty = toNumber(value);
          return `${qty > 0 ? "+" : ""}${qty.toLocaleString()}`;
        },
      },
      {
        title: this.tooltipLabel("Resulting Qty", "Quantity after this adjustment"),
        dataIndex: "resultingQuantity",
        key: "resultingQuantity",
        width: 130,
        align: "right",
        render: (value) => {
          if (value === null || value === undefined) {
            return "-";
          }
          return toNumber(value).toLocaleString();
        },
      },
      {
        title: this.tooltipLabel("Rate", "Applied SAV rate"),
        dataIndex: "rate",
        key: "rate",
        width: 120,
        align: "right",
        render: (value) => (toNumber(value) > 0 ? `Rs ${toNumber(value).toFixed(2)}` : "-"),
      },
      {
        title: this.tooltipLabel("Value", "Absolute value for SAV row"),
        dataIndex: "value",
        key: "value",
        width: 130,
        align: "right",
        render: (value) => this.formatCurrency(value),
      },
      {
        title: this.tooltipLabel("Remarks", "Adjustment remark"),
        dataIndex: "remarks",
        key: "remarks",
        render: (value) => value || "-",
      },
      {
        title: this.tooltipLabel("Created At", "SAV creation timestamp"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 170,
        render: (value) => (value ? moment(value).format("DD MMM YYYY HH:mm") : "-"),
      },
    ];

    return (
      <Modal
        visible={!!(savModal && savModal.visible)}
        title={this.tooltipLabel("SAV Details", "Reviewed adjustment voucher details for this spare")}
        width={980}
        onCancel={this.closeSavModal}
        onOk={this.closeSavModal}
        okText="Close"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        {record ? (
          <div className="spares-adjustment-modal">
            <div className="spares-form-grid">
              <div className="spares-form-field">
                <span>{this.tooltipLabel("Spare", "Spare item for which SAV is created")}</span>
                <div className="spares-readonly-note">{record.spareCode || "-"} • {record.spareName || "-"}</div>
              </div>
              <div className="spares-form-field">
                <span>{this.tooltipLabel("Total SAV", "Number of SAV vouchers created")}</span>
                <div className="spares-readonly-note">{totalSav}</div>
              </div>
              <div className="spares-form-field">
                <span>{this.tooltipLabel("Total Adjustment Qty", "Sum of reviewed adjustment quantities")}</span>
                <div className="spares-readonly-note">
                  {`${toNumber(record.adjustmentQuantity) > 0 ? "+" : ""}${toNumber(record.adjustmentQuantity)}`}
                </div>
              </div>
            </div>
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ x: true }}
              columns={savColumns}
              dataSource={savRows}
              locale={{ emptyText: "No SAV details available for this record." }}
            />
          </div>
        ) : null}
      </Modal>
    );
  };

  render() {
    const { mode } = this.state;

    return (
      <>
        {mode === "view" ? this.renderWorkspacePage() : this.renderListPage()}
        {this.renderCreateModal()}
        {this.renderIgnoreRemarkModal()}
        {this.renderSavModal()}
      </>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  authUser: auth.authUser,
});

export default connect(mapStateToProps, {})(SpareStockTaking);
